import React, { useMemo, useState } from "react";
import { usePermissions } from "../../../context/PermissionContext";
import AdminReviewFormsPage from "../../admins/AdminReviewFormsPage";
import FormReviewQueuePage from "../FormReviewQueuePage";
import MyFormResultsPage from "../MyFormResultsPage";
import FormExecutionReportsPage from "../FormExecutionReportsPage";
import FormResponsesPage from "../FormResponsesPage";
import "./FormsReviewReportsTab.css";

// Review & Reports tab of the Forms Workspace -- combines Review Forms
// (forms assigned TO you, to fill), Form Review Queue (submissions from
// OTHERS awaiting your approval), My Form Results, and Form Execution
// Reports into one tab with internal sub-tab chips. Every sub-tab renders
// the EXISTING page component unmodified (aside from FormExecutionReportsPage's
// own KPI-compaction pass, done separately) -- all data/business logic is
// unchanged.
const SUB_TABS = [
  {
    key: "to-fill",
    label: "To Fill",
    icon: "fa-solid fa-list-check",
    isVisible: (can) => can("assigned_forms", "view_own"),
    render: () => <AdminReviewFormsPage embedded />,
  },
  {
    key: "to-review",
    label: "To Review",
    icon: "fa-solid fa-magnifying-glass-chart",
    isVisible: (can) => can("form_reviews", "view_review_queue"),
    render: () => <FormReviewQueuePage embedded />,
  },
  {
    key: "results",
    label: "My Results",
    icon: "fa-solid fa-chart-simple",
    isVisible: (can) => can("form_results", "view_own_result") || can("form_results", "view_result"),
    render: () => <MyFormResultsPage embedded />,
  },
  {
    key: "reports",
    label: "Execution Reports",
    icon: "fa-solid fa-file-invoice",
    isVisible: (can) => can("form_execution_reports", "view_own") || can("form_execution_reports", "view"),
    render: () => <FormExecutionReportsPage embedded />,
  },
  {
    // Dynamic Forms visibility audit, §7: the canonical, generic Responses
    // table -- distinct from every sub-tab above (own results only /
    // pending decisions only / UAT-profiled forms only). Gated on the same
    // `reports:view_form_results` action GET /reports/form-submissions
    // itself enforces server-side (via resolveReportScope), so this tab
    // only ever appears for someone who can actually load it.
    key: "responses",
    label: "Responses",
    icon: "fa-solid fa-table-list",
    isVisible: (can) => can("reports", "view_form_results"),
    render: () => <FormResponsesPage embedded />,
  },
];

export default function FormsReviewReportsTab({ initialView }) {
  const { can } = usePermissions();

  const visibleSubTabs = useMemo(
    () => SUB_TABS.filter((t) => t.isVisible(can)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [activeSubTab, setActiveSubTab] = useState(() => {
    if (initialView && visibleSubTabs.some((t) => t.key === initialView)) return initialView;
    return visibleSubTabs[0]?.key || null;
  });

  if (!visibleSubTabs.length) {
    return (
      <div className="unit-empty">
        <div className="unit-empty-icon"><i className="fa-solid fa-lock" aria-hidden="true"></i></div>
        <h5>Access denied</h5>
        <p>You do not have permission to review or report on forms.</p>
      </div>
    );
  }

  const resolved = visibleSubTabs.some((t) => t.key === activeSubTab) ? activeSubTab : visibleSubTabs[0].key;
  const current = visibleSubTabs.find((t) => t.key === resolved);

  return (
    <div className="forms-review-reports-tab">
      <div className="frr-chip-row" role="tablist" aria-label="Review & Reports sections">
        {visibleSubTabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={resolved === t.key}
            className={`frr-chip ${resolved === t.key ? "active" : ""}`}
            onClick={() => setActiveSubTab(t.key)}
          >
            <i className={t.icon} aria-hidden="true"></i>
            {t.label}
          </button>
        ))}
      </div>
      <div className="frr-sub-panel">{current?.render()}</div>
    </div>
  );
}
